import { captureException } from '@repo/error';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AuthenticationError } from './errors/AuthenticationError';
import { BusinessValidationError } from './errors/BusinessValidationError';
import { NetworkError } from './errors/NetworkError';
import { RepositoryError } from './errors/RepositoryError';
import { ValidationError } from './errors/ValidationError';
import { isNetworkError } from './utils/isNetworkError';
const shouldReportToSentry = (error) => error instanceof RepositoryError &&
    !(error instanceof ValidationError) &&
    !(error instanceof BusinessValidationError) &&
    !(error instanceof NetworkError) &&
    !(error instanceof AuthenticationError);
const extractGraphQlErrors = (error) => {
    const cause = error.cause;
    if (typeof cause !== 'object' || cause === null || !('graphQLErrors' in cause))
        return undefined;
    return Array.isArray(cause.graphQLErrors) ? cause.graphQLErrors : undefined;
};
const reportToSentry = (error) => {
    if (!shouldReportToSentry(error))
        return;
    const graphQLErrors = extractGraphQlErrors(error);
    captureException(error, graphQLErrors
        ? { contexts: { graphql: { response: JSON.stringify({ errors: graphQLErrors }, null, 2) } } }
        : undefined);
};
let unauthenticatedHandler;
let lastUnauthenticatedHandledAt = 0;
/**
 * How long (ms) to swallow further `AuthenticationError`s after the handler
 * has fired once. The typical handler calls `clearQueryCache()` and triggers
 * a navigation; active query observers then remount and re-fire against an
 * empty cache, each producing another `AuthenticationError`. Without this
 * suppression the handler would run on every cascade error, re-clearing the
 * cache and feeding the loop. 5s comfortably covers the redirect + unmount.
 */
const UNAUTHENTICATED_SUPPRESSION_MS = 5000;
/**
 * Register a handler invoked whenever an `AuthenticationError` is thrown from
 * any query or mutation. Call once at app startup from the host app (see
 * `main.tsx`) with the app's logout routine.
 *
 * Centralising this here lets repositories stay ignorant of auth side effects
 * — they just throw the typed error, and this handler drives the redirect.
 */
export const setUnauthenticatedHandler = (handler) => {
    unauthenticatedHandler = handler;
    lastUnauthenticatedHandledAt = 0;
};
const handleCacheError = (error) => {
    if (error instanceof AuthenticationError) {
        const now = Date.now();
        if (now - lastUnauthenticatedHandledAt < UNAUTHENTICATED_SUPPRESSION_MS)
            return;
        lastUnauthenticatedHandledAt = now;
        unauthenticatedHandler?.();
        return;
    }
    reportToSentry(error);
};
/**
 * Skip the default exponential-backoff retry loop for errors that retries
 * cannot fix:
 * - `AuthenticationError` — the session is invalid; retrying delays the login
 *   redirect (the cache observer only fires after all retries are exhausted).
 * - `ValidationError` / `BusinessValidationError` — deterministic backend
 *   rejections of expected user-facing failures; retrying is noise.
 * - `NetworkError` — handled globally by `ErrorBoundary` / `NetworkErrorPage`,
 *   which owns the retry UX; TanStack retrying in the background flattens
 *   that page's own retry button.
 */
export const shouldRetry = (failureCount, error) => {
    if (error instanceof AuthenticationError)
        return false;
    if (error instanceof ValidationError)
        return false;
    if (error instanceof BusinessValidationError)
        return false;
    if (error instanceof NetworkError)
        return false;
    return failureCount < 3;
};
export const queryClient = new QueryClient({
    mutationCache: new MutationCache({ onError: handleCacheError }),
    queryCache: new QueryCache({ onError: handleCacheError }),
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            gcTime: 30_000,
            retry: shouldRetry,
            throwOnError: (error) => isNetworkError(error),
        },
    },
});
export function clearQueryCache() {
    queryClient.removeQueries({ predicate: (query) => query.state.fetchStatus === 'idle' });
    queryClient.getMutationCache().clear();
}
